import { css } from 'styled-components';

import { layer, path, opacity, radius } from './variable';
import { IMAGE_PATH_ROOT_ALL } from '@/domain/constants/ImagePath';

/**
 *******************
MEMO:mixin.cssファイルの修正も実行することを忘れないように気をつける
 ********************
 */

/* 使い方メモ
① 使いたいmixinをimportする
例）import { mixinHoge } from '../../../../common/css/setting/mixin';
② mixinを展開したい場所で読み込む
例）const Hoge = styled.div` ${mixinHoge} `;
③ 必要に応じて引数を渡す
例）${mixinHoge({width:100})}
*/

/* SP コンテンツの上にborderが必要な場合に使う
  ネガティブマージンをつけて、マストヘッド・お知らせと連続するときに罫線を打ち消す
● 使い方イメージ
const Hoge = styled.div`
  ${mixinContentsHasBorderTop};
`;
*/
export const mixinContentsHasBorderTop = css`
  border-top: 1px solid ${props => props.theme.news.palette.bor_lv2_sp};
  margin-top: -1px;
`;

/* floatの回り込みを解除したい時に使う
● 引数
なし
● 使い方イメージ
const Hoge = styled.div`
  ${mixinClearfix};
`;
*/
export const mixinClearfix = `
  content: '';
  clear: both;
  display: block;
`;

/* テキストを飛ばして画像を表示したい時に使う
● 引数
なし
● 使い方イメージ
const Hoge = styled.div`
  ${mixinTextIndentImg};
`;
*/
export const mixinTextIndentImg = `
  text-indent: -9999px;
  overflow: hidden;
  white-space: nowrap;
`;

/* 読み上げブラウザなどで読み上げて欲しいけど、表示したくない時に使う
● 引数
なし
● メモ
・単純に display:none すると読み上げられなくなることがあるので、このmixinを使用する
● 使い方イメージ
const Hoge = styled.div`
  ${mixinReadOnly};
`;
*/
export const mixinReadOnly = `
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  overflow: hidden;
  position: absolute !important;
  width: 1px;
`;

/* テキストを1行で３点リーダーにする時に使う（SPのみ確認済み）
● 引数
なし
● 注意点
現在SPのみ確認済み（PCでも使用場面があればこちらの記述は消してください。）
● 使い方イメージ
const Hoge = styled.div`
  ${mixinTextEllipsisSingleLine};
`;
*/
export const mixinTextEllipsisSingleLine = `
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* テキストを複数行で３点リーダーにする時に使う（SP・PC共通）
● 引数
lines（行数）：任意（デフォルトは3行）
● 使い方イメージ
const Hoge = styled.div`
  ${mixinTextEllipsisMultiLine({ lines: 5 })};
`;
*/
export const mixinTextEllipsisMultiLine = (props: { lines?: number }) => {
  // 指定がなければデフォルトの値にする
  const lines = props.lines ? props.lines : 3;

  return `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: ${lines};
  `;
};

/* サムネイルの枠線
● 引数
width：必須
height：必須
● 使い方イメージ
const Hoge = styled.div`
  ${mixinThumbnailLine({ width: '100px', height: '100px' })};
`;
*/
export const mixinThumbnailLine = (props: { width: string; height: string }) => css`
  position: relative;
  &::after {
    position: absolute;
    top: 0;
    left: 0;
    box-sizing: border-box;
    display: block;
    width: ${props.width};
    height: ${props.height};
    content: '';
    border: solid 1px ${({ theme }) => theme.news.palette.thumbnail_normal_border_common};
  }
`;

/* サムネイルの時刻表記
● 引数
padding：任意（デフォルトは4px）
fontSize：任意（デフォルトは11px）
● 使い方イメージ
const Hoge = styled.div`
  ${mixinThumbnailTimeLabel({ padding: 6, fontSize: 14 })};
`;
● 再生アイコンについて
このmixinを読み込むだけでは再生アイコンは付かないので、別途読み込んでください
*/
export const mixinThumbnailTimeLabel = (props: { padding?: number; fontSize?: number }) => {
  // 指定がなければデフォルトの値にする
  const padding = props.padding ? props.padding : 4;
  const fontSize = props.fontSize ? props.fontSize : 11;

  return `
    display: inline-flex;
    position: absolute;
    right: 0;
    bottom: 0;
    padding: calc(${padding}px / 2) calc(${padding}px) calc(${padding}px / 2) calc(${padding}px + 2px);
    color: #fff;
    font-family: Helvetica;
    font-size: ${fontSize}px;
    line-height: 1;
    background-color: rgba(0, 0, 0, 0.7);
  `;
};

/* ローディングスピナー
● 引数
size：必須
color：必須
● 使い方イメージ
const Hoge = styled.div`
  ${mixinloadingSpinner({ size: 20, color: "#fff" })};
`;
● 使う場面
SPではRiffのSpinnerコンポーネントを利用してください。
RiffがIEに対応していないため、PCではこのmixinを利用してください。
*/
export const mixinloadingSpinner = (props: { size: number; color: string }) => css`
  position: relative;
  display: block;
  width: ${props.size}px;
  height: ${props.size}px;
  padding: 0;
  background: linear-gradient(to right, ${props.color} 10%, rgba(255, 255, 255, 0) 42%);
  border: none;
  border-radius: ${radius.loadingSpinner_common};
  transform: translateZ(0);
  animation: loadingRotation 0.75s linear infinite;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 50%;
    content: '';
    background-color: ${props.color};
    border-radius: ${radius.loadingSpinnerInner_common};
  }

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 75%;
    height: 75%;
    margin: auto;
    content: '';
    background-color: ${({ theme }) => theme.news.palette.bg_lv1_common};
    border-radius: ${radius.loadingSpinner_common};
  }

  /* ローディングスピナーのアニメ */
  @keyframes loadingRotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

/* ローディングイメージ
※透過の都合などでイメージでやつ必要な場合はこちらのローディングを使用
● 引数
size：必須
● 使い方イメージ
const Hoge = styled.div`
  ${mixinLoadingImageRotation({ size: 30 })};
`;
*/
export const mixinLoadingImageRotation = (props: { size: number }) => css`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: ${props.size}px;
  height: ${props.size}px;
  margin: auto;
  background-image: url(${path.img_loading});
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  transform: translate(-50%, -50%);
  animation: loadingImageRotation 0.75s linear infinite;
  /* loading回すアニメ */
  @keyframes loadingImageRotation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const mixinSkeletonScreenWave = css`
  /* スケルトンスクリーンのアニメ */
  @keyframes skeletonScreenWave {
    0% {
      opacity: ${opacity.skeletonScreen_max_common};
    }
    100% {
      opacity: ${opacity.skeletonScreen_min_common};
    }
  }
`;

/* スケルトンスクリーン
● 引数
listHeight（記事の高さ）：必須
articleCount（記事の何本分のスケルトンスクリーンを表示するか）：必須
left（左からの距離）：必須
width：必須
position（背景画像のボジション）：必須
● 使い方イメージ
const Hoge = styled.div`
  ${mixinSkeletonScreen({ listHeight: 104, articleCount: 10, left: 4, width: '300px', position: 100 })};
`;
*/
export const mixinSkeletonScreen = (props: {
  listHeight: number;
  articleCount: number;
  left: number;
  width: string;
  position: number;
}) => css`
  position: relative;
  min-height: calc(
    ${props.listHeight}px * ${props.articleCount}
  ); /* 記事 articleCount 本分の高さ（タブ切り替え時のスクロール位置がズレないようにするため） */
  padding-bottom: 16px; /* 画像が見切れないようにするため必須 */

  /* 背景画像とアニメーション */
  &::before {
    position: absolute;
    top: 0;
    left: ${props.left}px;
    z-index: ${layer.middle_common};
    display: block;
    width: ${props.width};
    height: 100%;
    content: '';
    background-image: url('${IMAGE_PATH_ROOT_ALL}/skeletonScreen_thumb.svg'),
      url('${IMAGE_PATH_ROOT_ALL}/skeletonScreen_list.svg');
    background-repeat: repeat-y, repeat-y;
    background-position: 0 0, 0 ${props.position}px;
    background-size: ${props.position}px ${props.listHeight}px, 100% ${props.listHeight}px;
    animation: skeletonScreenWave 1s infinite;
  }

  /* スケルトンスクリーンのアニメ */
  ${mixinSkeletonScreenWave}
`;

/* リストdisc表示
● 引数
color；任意
paddingLeft：任意
fontSize：任意
lineHeight：任意
● 使い方イメージ
const Hoge = styled.div`
  ${discList({ paddingLeft: 14 })}
`;
*/
export const discList = (props: { color?: string; paddingLeft?: number; fontSize?: number; lineHeight?: number }) => {
  // 指定がなければデフォルトの値にする
  const paddingLeft = props.paddingLeft ? props.paddingLeft : 12;
  const bgColor = props.color ? props.color : '#ccc';
  const fontSize = props.fontSize ? props.fontSize : 14;
  const lineHeight = props.lineHeight ? props.lineHeight : 1.4;

  return css`
    position: relative;
    padding-left: ${paddingLeft}px;

    &::before {
      content: '';
      position: absolute;
      top: ${(fontSize * lineHeight) / 2 - 2}px; /* テキスト1行目の真ん中から、マルぽちの半分だけ上に */
      left: 0;
      display: block;
      width: 4px;
      height: 4px;
      border-radius: ${radius.trueCircle_common};
      background-color: ${bgColor};
    }
  `;
};

/* SPのトピックスメイン画像にかける黒グラデーション
● 引数
なし
● 使い方イメージ
const Hoge = styled.div`
  ${mixinPickupMainImageGradation};
`;
*/
export const mixinPickupMainImageGradation = `
  content: '';
  display: block;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: ${opacity.pickup_gradation_common};
  z-index: ${layer.base_common};

`;

/* PCでvisited色にしたいリンク
● 引数
なし
● 使い方イメージ
const Link = styled.a`
  ${pcVisitedLink};
`;
*/
export const pcVisitedLink = css`
  &:visited {
    color: ${props => props.theme.news.palette.link_visited_pc};
  }
  &:hover {
    color: ${props => props.theme.news.palette.link_hover_pc};
  }
`;
